'use client';

import { useContext, PropsWithChildren } from 'react';
import { Flex } from '@mezzoforte/forge';
import { BiddingContext } from '@/context/BiddingContext';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import { useSession } from '@/hooks/useSession';
import { priceWithMinDeliveryExpenses } from '@/util/price';
import { FinancingOption } from '@/types/ListEntry';

export interface FinancingOptionWrapperProps {
  option: FinancingOption;
}

export function FinancingOptionWrapper({
  option: { allowedReceiptTypes, maxPrice },
  children,
}: PropsWithChildren<FinancingOptionWrapperProps>) {
  const { entryId, vatPerc, startPrice, minimumRaise, deliveryMethods, processingCostInfo } =
    useContext(BiddingContext);
  const { hermesEntry } = useHermesEntry(entryId, vatPerc);
  const { entryInfo } = useSession();
  const { data: entryInfoForUser } = entryInfo(entryId);

  if (!hermesEntry || !entryInfoForUser) return null;

  if (hermesEntry.isEnded) return null;
  // Check for client-only constraints: maxPrice, allowedReceiptTypes
  const selectedReceiptType = entryInfoForUser.isLoggedIn ? entryInfoForUser.selectedReceiptType : null;
  if (
    maxPrice !== null &&
    priceWithMinDeliveryExpenses(
      hermesEntry.highestBid,
      startPrice,
      minimumRaise,
      vatPerc,
      deliveryMethods,
      processingCostInfo
    ) > maxPrice
  )
    return null;
  // Only filter by receipt type if selectedReceiptType is known
  if (selectedReceiptType !== null && !allowedReceiptTypes.includes(selectedReceiptType)) return null;

  return (
    <Flex
      px={3}
      py={2}
      alignItems="center"
    >
      {children}
    </Flex>
  );
}
